import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { CardTableHeader, CardTableContent } from './CardTable.parts';
import { BaseCardTableProps } from './types';

export interface CardTableCollapsibleProps<T> extends BaseCardTableProps<T> {
  defaultExpanded?: boolean;
  onToggle?: (isExpanded: boolean) => void;
}

export function CardTableCollapsible<T>({
  title,
  icon,
  iconColor = 'text-primary',
  titleAside,
  columns,
  data,
  actions,
  onItemClick,
  emptyStateMessage = 'Aucune donnée disponible',
  isLoading,
  loadingComponent,
  defaultExpanded = true,
  onToggle,
  headerClassName,
  rowClassName
}: CardTableCollapsibleProps<T>) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggle?.(newState);
  };

  if (isLoading && loadingComponent) {
    return <>{loadingComponent}</>;
  }

  return (
    <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
      <CardTableHeader
        title={title}
        icon={icon}
        iconColor={iconColor}
        titleAside={titleAside}
        headerClassName={headerClassName}
        clickable
        onClick={handleToggle}
        rightContent={
          <button
            className="p-1 rounded hover:bg-primary/20 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            aria-label={isExpanded ? 'Réduire' : 'Développer'}
          >
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </button>
        }
      />

      {isExpanded && (
        <CardTableContent
          columns={columns}
          data={data}
          actions={actions}
          onItemClick={onItemClick}
          rowClassName={rowClassName}
          emptyStateMessage={emptyStateMessage}
        />
      )}
    </div>
  );
}
